import {
  createCaslAdapter,
  PermissionDataFilter,
  PermissionFieldDataFilter
} from "./caslAdapter";

export enum UserRole {
  None,
  Relectrify = "Relectrify",
  RelectrfiyUser = "RelectrifyUser",
  RelectrifyReadOnly = "RelectrifyReadOnly",
  CustomerAdmin = "CustomerAdmin",
  CustomerAdminAdvanced = "CustomerAdminAdvanced",
  CustomerReadOnly = "CustomerReadOnly",
  CustomerCertification = "CustomerCertification",
}

export interface PermissionEnforcer {
  throwUnlessCan: (permission: Permissions, data?: Record<string | number | symbol, unknown> | undefined) => void,
  can: (permission: Permissions, data?: Record<string | number | symbol, unknown> | undefined, debug?: (message: string) => void) => boolean,
  getDataFiltersFor: (permission: Permissions) => PermissionDataFilter
}
export type { PermissionFieldDataFilter };
const permittedCustomerCommandMessageTypes = ["mode_select"];
const permittedCustomerEventTypes = [""];
const permittedCustomerAdvancedEventTypes = ["Estop", "MC contactor", "Battery Full", "Battery Empty"];
const permittedCustomerReportDataSources = ["pack_data", "cell_data"];
const permittedCertificationCommands = ["disconnect", "constant_current", "cycle_charging", "net_metering", "constant_power", "certification", "eps_control"];


// ATTENTION: these permissions are enforced on our API
// however they are *not* enforced in firebase
// that is enforced by a seperate database.rules.json
// which for now just filters by site id
export enum Permissions {
  None,

  Site_ViewSummary,
  Site_ViewEvents,
  Site_ViewReports,
  Site_ViewRealtimeData,
  Site_SendCommand,

  Unit_ViewSummary,
  Unit_ViewEvents,
  Unit_ViewReports,
  Unit_ViewRealtimeData,
  Unit_SendCommand,

  Devices_ViewSummary,
  Devices_ViewReports,
  Devices_ViewEvents,
  Devices_ViewWaveforms,
  Devices_ViewMessages,
  Devices_ViewLogs,
  Devices_SendCommand,
  Devices_ManageAcrossAllSites,
  Devices_RunArbitraryReportQuery,
  Devices_ManageConfig,

  Users_Manage,
  Users_AddSiteToPermittedList = Users_Manage
}

export const permissionToSubjectAction = (permission: Permissions) => {
  const components = Permissions[permission].split("_");
  return { subject: components[0], action: components[1] };
};

// we're not trying to be 'restful'
// so resources/actions aren't consistent across resources
// therefore we simplify the usage of CASL so we can
// more directly tie actions & subjects
// to the current API endpoints
export function buildPermissionEnforcerFor({ role, userId, permittedSiteIds }: {
  role: UserRole,
  userId: string | undefined,
  permittedSiteIds: string[]
}): PermissionEnforcer {

  const { allow, deny, build } = createCaslAdapter(permissionToSubjectAction, `"${userId
    }" in role "${role}" with permitted site ids ${permittedSiteIds
    }`)

  if (role == UserRole.None || !userId)
    return build();
  // permissions for all authenticated users
  allow(Permissions.None);
  // each of these should be checked against the permitted
  // site id list
  allow(Permissions.Site_ViewSummary, {
    siteId: { $in: permittedSiteIds },
  });
  allow(Permissions.Site_ViewRealtimeData, {
    siteId: { $in: permittedSiteIds },
  });
  allow(Permissions.Site_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $exists: false }
    });
  allow(Permissions.Site_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $in: permittedCustomerReportDataSources }
    },
    {
      eventType: { $in: permittedCustomerEventTypes },
      dataSource: { $in: permittedCustomerReportDataSources }
    });
  allow(Permissions.Site_ViewEvents,
    {
      siteId: { $in: permittedSiteIds }
    }, {
    eventType: { $in: permittedCustomerEventTypes }
  });
  allow(Permissions.Unit_ViewSummary, {
    siteId: { $in: permittedSiteIds },
  });
  allow(Permissions.Unit_ViewRealtimeData, {
    siteId: { $in: permittedSiteIds },
  });
  allow(Permissions.Unit_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $exists: false }
    });
  allow(Permissions.Unit_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $in: permittedCustomerReportDataSources }
    },
    {
      eventType: { $in: permittedCustomerEventTypes },
      dataSource: { $in: permittedCustomerReportDataSources }
    });
  allow(Permissions.Unit_ViewEvents,
    {
      siteId: { $in: permittedSiteIds }
    },
    {
      eventType: { $in: permittedCustomerEventTypes }
    });
  allow(Permissions.Devices_ViewSummary, {
    siteId: { $in: permittedSiteIds },
  });
  allow(Permissions.Devices_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $exists: false }
    });
  allow(Permissions.Devices_ViewReports,
    {
      siteId: { $in: permittedSiteIds },
      dataSource: { $in: permittedCustomerReportDataSources }
    },
    {
      eventType: { $in: permittedCustomerEventTypes },
      dataSource: { $in: permittedCustomerReportDataSources }
    });

  allow(Permissions.Devices_ViewEvents,
    {
      siteId: { $in: permittedSiteIds },
    }, {
    eventType: { $in: permittedCustomerEventTypes }
  });

  switch (role) {

    case UserRole.CustomerCertification:
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        messageType: { $exists: false } // will always be present for actual data, just used for UI permission checking
      });
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        commandType: { $in: permittedCertificationCommands },
      });
      break;

    case UserRole.CustomerAdmin:
      deny(Permissions.Site_SendCommand);
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        messageType: { $exists: false } // will always be present for actual data, just used for UI permission checking
      });
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        messageType: { $in: permittedCustomerCommandMessageTypes },
      });
      allow(Permissions.Unit_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });

      break;

    case UserRole.CustomerAdminAdvanced:
      deny(Permissions.Site_SendCommand);
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        messageType: { $exists: false } // will always be present for actual data, just used for UI permission checking
      });
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
        messageType: { $in: permittedCustomerCommandMessageTypes },
      });
      allow(Permissions.Unit_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewEvents,
        {
          siteId: { $in: permittedSiteIds },
        }, {
        eventType: { $in: permittedCustomerAdvancedEventTypes }
      });

      break;

    case UserRole.Relectrify:
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Site_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Site_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Unit_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Unit_ViewEvents, {
        siteId: { $in: permittedSiteIds },
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Unit_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Devices_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Devices_ViewLogs, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ManageConfig, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewWaveforms, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewMessages, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
          dataSource: { $any: true },
        });

      // currently allowed for *all* siteIds regardless of user permissions
      allow(Permissions.Devices_ManageAcrossAllSites);
      allow(Permissions.Devices_RunArbitraryReportQuery);

      // all users managed by relectrify for now, without site id filters
      allow(Permissions.Users_Manage);
      break;

    case UserRole.RelectrfiyUser:
      allow(Permissions.Site_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Site_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Site_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Unit_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Unit_ViewEvents, {
        siteId: { $in: permittedSiteIds },
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Unit_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Devices_SendCommand, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Devices_ViewLogs, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ManageConfig, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewWaveforms, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewMessages, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
          dataSource: { $any: true },
        });

      allow(Permissions.Devices_RunArbitraryReportQuery);
      // currently allowed for *all* siteIds regardless of user permissions
      allow(Permissions.Devices_ManageAcrossAllSites);

      break;

    case UserRole.RelectrifyReadOnly:
      allow(Permissions.Site_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Site_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Unit_ViewEvents, {
        siteId: { $in: permittedSiteIds },
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Unit_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
        });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      });
      allow(Permissions.Devices_ViewEvents, {
        siteId: { $in: permittedSiteIds }
      }, {
        eventType: { $any: true }
      });
      allow(Permissions.Devices_ViewLogs, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewWaveforms, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewMessages, {
        siteId: { $in: permittedSiteIds },
      });
      allow(Permissions.Devices_ViewReports,
        {
          siteId: { $in: permittedSiteIds }
        },
        {
          eventType: { $any: true },
          dataSource: { $any: true },
        });

      allow(Permissions.Devices_RunArbitraryReportQuery);

      break;
  }
  return build();
}

